import React, { ComponentPropsWithoutRef, PropsWithChildren, useMemo } from 'react';
import CustomModal from '../../components-library/CustomModal';
import { ENVIRONMENTS, MODAL_SIZES } from '../../services/exports/Constants';
import useScreenType from '../../hooks/utility/useScreenType';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import { ButtonTypes, MobileAppSignupBonusModalProps } from '../../services/exports/Interfaces';
import CustomButton from '../../components-library/buttons/CustomButton';
import useInitialData from '../../hooks/global/useInitialData';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAppMethods from '../../hooks/utility/useAppMethods';
import QRCode from 'react-qr-code';
import usePlayStore from '../../hooks/mobile-app/usePlayStore';
import { useTranslation } from 'react-i18next';
import MobileIcon from '../../assets/icons/MobileIcon';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import AppStoreIcon from '../../assets/icons/mobile-stores/AppStoreIcon';
import GooglePlayStoreIcon from '../../assets/icons/mobile-stores/GooglePlayStoreIcon';
import { Modals } from '../../modules/mobile/exports/Enums';

const mainDomain = import.meta.env.VITE_WEBSITE_DOMAIN;
const viteMode = import.meta.env.VITE_MODE;

const MobileAppSignupBonusModal = (_: MobileAppSignupBonusModalProps) => {
  const { data } = useInitialData();

  const mobileAppUrl = useMemo(() => {
    if (!data?.franchise.mobile_app?.uuid) {
      return;
    }

    const url = new URL(data?.franchise.mobile_app.uuid, mainDomain);
    let subDomain = 'mobile-app';

    switch (viteMode) {
      case ENVIRONMENTS.DEV:
        subDomain = subDomain.concat('-dev');
        break;
      case ENVIRONMENTS.STAGING:
        subDomain = subDomain.concat('-staging');
        break;
    }

    url.hostname = `${subDomain}.${url.hostname}`;
    url.searchParams.set('open_modal', Modals.Login);

    return url.toString();
  }, [data?.franchise.slug]);
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:MobileAppSignupBonusModal' });

  return (
    <WithWrapper>
      <div className="flex flex-col p-lg space-y-lg">
        <div className="space-y-md flex flex-col">
          <QRCode value={mobileAppUrl} className="mx-auto border p-md rounded-lg shadow size-[200px]" />
          <p className="text-center">{t('qr_code_desc')}</p>
        </div>
        <Cta />
        <DownloadFrom className="space-y-sm" />
      </div>
    </WithWrapper>
  );
};

const Cta = () => {
  const { formatCurrency } = useAppMethods();
  const { cashbackProgram } = useCashbackProgram();
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:MobileAppSignupBonusModal' });
  const mobileAppSignupBonus = formatCurrency(cashbackProgram.mobile_app_signup_bonus / 100);

  return (
    <div className="w-full border-l-[4px] border-l-orange-400 py-md px-md">
      <p className="text-center font-semibold text-md">{t('cta', { amount: mobileAppSignupBonus })}</p>
    </div>
  );
};

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return children;
  }

  return (
    <CustomModal open position="center" zIndex={1300} size={MODAL_SIZES.XS} fullScreen={!isDesktop}>
      <Header />
      {children}
    </CustomModal>
  );
};

type ChildProps = ComponentPropsWithoutRef<'div'>;

const Header = () => {
  const { toggleMobileAppSignupBonusModal } = useAppMethods();
  const { data } = useInitialData();
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:MobileAppSignupBonusModal' });
  const onClose = () => toggleMobileAppSignupBonusModal({ openModal: false });

  return (
    <div className="flex items-center p-md">
      <MobileIcon className="text-orange-400 mr-sm" />
      <h3 className="text-md">{t('title', { app: data?.franchise.name })}</h3>
      <button className="ml-auto" onClick={onClose}>
        <CloseCircleIcon />
      </button>
    </div>
  );
};

const DownloadFrom = (props: ChildProps) => {
  const { data } = useInitialData();
  const { openGooglePlayStore, openAppleStore } = usePlayStore();
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:MobileAppSignupBonusModal' });

  return (
    <div {...props}>
      <p className="text-center font-semibold">{t('download_from')}</p>
      <CustomButton
        className="hover:bg-orange-400 gap-x-2 !justify-start"
        buttonType={ButtonTypes.fifth}
        onClick={() => openAppleStore(data?.franchise.mobile_app.asc_app_id)}
        customIcon={<AppStoreIcon />}
      >
        App Store
      </CustomButton>
      <CustomButton
        className="hover:bg-orange-400 gap-x-2 !justify-start"
        buttonType={ButtonTypes.fifth}
        onClick={() => openGooglePlayStore(data?.franchise.mobile_app.app_bundle_identifier)}
        customIcon={<GooglePlayStoreIcon />}
      >
        Google Play
      </CustomButton>
    </div>
  );
};

export default MobileAppSignupBonusModal;
